import React, { useEffect, useState }    from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import axios from 'axios'
import dayjs from 'dayjs'
import { useLocation } from 'react-router-dom'
import { RootState } from '../../../../../setup'
import { EDIT_NOTE_ENTRY_FORM_INPUTS } from '../../../Notes/Speech_therapy/SPEECH_THERAPY_FORM_CONSTANTS'
import FormWrapper from '../../../../reusable-components/Wrapper/Wrapper'
import FrequencyDateRangePicker from '../../../../reusable-components/NoteType/NoteType'
import DynamicForm from '../../../../reusable-components/forms/DynamicForm'
import { MoonLoader } from 'react-spinners'
import { Typography, Box } from '@mui/material'
import NavigateButton from '../../../../reusable-components/description-modal/navigatebutton'
import DescriptionModal from '../../../../reusable-components/description-modal/descriptionmodal'
const selectTimeZone = (state: RootState) => state.timeZone.timeZone

type LocationStateType = {
    entries: any[]
  }

const SpeechTherapyNoteForm = () => {
  const authUser = useSelector((state: {auth: any}) => state.auth)
  const history = useHistory()
  const params: {id: string} = useParams()
  const [clients, setClients] = useState([])
  const [selectedClientId, setSelectedClientId] = useState<{label: string; value: string} | null>(
    null
  )
  const [isFormLoading, setIsFormLoading] = useState(false)
  const [formElements, setFormElements] = useState([])
  const [formData, setFormData] = useState({})
  const [shortTermGoals, setShortTermGoals] = useState([])
  const [goalScoreList, setGoalScoreList] = useState([]) // For tracking scores and descriptions
  const [baseFormElements, setBaseFormElements] = useState([])
  const location = useLocation()
  const state = location.state as LocationStateType
  const entries = state?.entries || []
  const [description, setDescription] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [submittedFormData, setSubmittedFormData] = useState(null)
  const [pendingSubmit, setPendingSubmit] = useState(false)
  const [clientId, setClientId] = useState<string | null>(null)
  useEffect(() => {
    fetchData()
  }, [])
  const timeZone = useSelector(selectTimeZone)

  const fetchData = async () => {
    setIsFormLoading(true)
    try {
      const res = await axios.get(`/speechTherapyEntry/getById/${params.id}?isDeleted=true`)
      const data = res.data.result

      const response = await axios.get('/clients/getClientNames')
      const {data: clientsList} = response?.data
      setClientId(data.clientId)
      const updatedClients = clientsList.map((client) => ({
        label: client.fullName,
        value: client._id,
      }))
      const selectedClient = updatedClients.find((client) => client.value === data?.clientId)
      const formattedStartDate = dayjs.utc(data.startDate).tz(timeZone, true)
      const formattedEndDate = dayjs.utc(data.endDate).tz(timeZone, true)
      const formattedPlanDate = dayjs.utc(data.treatmentPlanStartDate).tz(timeZone, true)
      const formattedProgressDate = dayjs.utc(data.progressReportDate).tz(timeZone, true)
      const formattedNextSession = dayjs.utc(data.nextSession).tz(timeZone, true)
      setSelectedClientId(selectedClient)
      setClients(updatedClients)
      setShortTermGoals(data.longTermGoals || [])

      const initialGoalScoreList = data.longTermGoals.map((goal) => ({
        shortTermObjectives: goal.shortTermObjectives,
        status: goal.status || '',
        description: goal.notes || '',
      }))

      setGoalScoreList(initialGoalScoreList)
      let availability = ''
      if (data.continuePlan?.value) availability = 'continuePlan'
      else if (data.amendTreatmentPlan?.value) availability = 'amendTreatmentPlan'
      else if (data.dismissPatient?.value) availability = 'dismissPatient'
      const updatedFormData = {
        clientId: data.clientId,
        noteType: data.noteType,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        timeIn: data.timeIn || '',
        timeOut: data.timeOut || '',
        progressReportDate: formattedProgressDate,
        treatmentPlanStartDate: formattedPlanDate,
        sessionsCompleted: Array.isArray(entries) ? entries.length : 'none',
        availability,
        nextSession: formattedNextSession,
      }
      setFormData(updatedFormData)
      const updatedFormInputs = EDIT_NOTE_ENTRY_FORM_INPUTS().map((item) => {
        if (item.name === 'clientId') {
          return {...item, options: updatedClients, disabled: true}
        }
        return {
            ...item,
            disabled: true,
          }
      })

      setBaseFormElements(updatedFormInputs)
      setFormData((prev) => ({
        ...prev,
        clientId: selectedClient?.value || '',
      }))
    } catch (error) {
      toast.error('Failed to fetch data')
    } finally {
      setIsFormLoading(false)
    }
  }

  const setUIAndData = () => {
    let uiArray = [...baseFormElements] // ✅ Use the correct base

    let lastRow = 30

    const statusOptions = [
      {label: 'Achieved', value: 'ACHIEVED'},
      {label: 'In Progress', value: 'IN_PROGRESS'},
      {label: 'Other', value: 'OTHER'},
    ]

    if (goalScoreList.length > 0) {
      goalScoreList.forEach((goal, index) => {
        uiArray.push(
          {
            label: 'Goal:',
            name: `${index}Goal`,
            type: 'text',
            // required: true,
            rowNumber: lastRow,
            value: `${goal.shortTermObjectives}`,
            disabled: true,
            sx: {width: '100%', marginTop: '0.5rem'},
            className: `col-lg-6 col-sm-12 col-md-6 col-12`,
          },
          {
            label: 'Status',
            name: `${index}Status`,
            type: 'select',
            options: statusOptions,
            // required: true,
            rowNumber: lastRow,
            value: goal.status || '',
            disabled: true,
            className: `col-lg-6 col-sm-12 col-md-6 col-12`,
            sx: {width: '100%', marginTop: '0.5rem'},
            index: index, // add this to pass index inside your Autocomplete onChange handler
          },

          {
            label: 'Description:',
            name: `${index}Description`,
            required: false,
            rowNumber: lastRow,
            value: goal.description || '',
            type: 'textarea',
            disabled: true,
            className: `col-lg-12 col-sm-12 col-md-12 col-12`,
            sx: {resize: 'none'},
          }
        )
        lastRow++
      })
    }

    setFormElements(uiArray)
  }

  useEffect(() => {
    if (goalScoreList.length > 0 && baseFormElements.length > 0) {
      setUIAndData()
    }
  }, [goalScoreList, baseFormElements])

  const buttons = [
    {label: 'Cancel', variant: 'outlined', type: 'button'}
  ]
  return (
    <FormWrapper
      title='Deleted ST Progress Note'
      content={
        <>
          {isFormLoading ? (
            <div className='overlay d-flex justify-content-center' style={{marginTop: '100px'}}>
              <MoonLoader color='#9db2fc' size={60} loading={isFormLoading} />
            </div>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                  marginLeft: '1rem',
                }}
                color='primary'
              >
                Note Type
              </Typography>
              <Box className='mb-3'>
                <FrequencyDateRangePicker
                  isEntry={true}
                  isTemplate={false}
                  isFullForm={false}
                  isEdit={true}
                  formData={formData}
                  setFormData={setFormData}
                />
              </Box>
              <DynamicForm
                fields={formElements}
                data={formData}
                onSubmit={()=>{}}
                buttons={buttons}
              />
            </>
          )}
        </>
      }
    />
  )
}

export default SpeechTherapyNoteForm
